import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, QrCode, ShieldCheck } from "@phosphor-icons/react";
import { useLang } from "../../context/LangContext";
import { IMAGES } from "../../lib/images";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fade = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.6 + i * 0.1, ease: [0.16, 1, 0.3, 1] } }),
};

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const titles = [t.hero.title1, t.hero.title2, t.hero.title3];

  return (
    <section ref={ref} className="relative min-h-screen bg-white pt-28 lg:pt-32 overflow-hidden" data-testid="hero">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          {/* Left */}
          <div className="lg:col-span-6 pb-16 lg:pb-24 pt-6 lg:pt-10">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 border border-slate-200 rounded-full pl-2 pr-4 py-1.5 mb-8">
              <span className="flex h-5 items-center rounded-full bg-emerald-500/10 px-2 font-mono-tech text-[10px] font-semibold uppercase tracking-wider text-emerald-600">UE</span>
              <span className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-slate-500">{t.hero.overline}</span>
            </motion.div>

            <h1 className="font-display font-bold text-[clamp(2.6rem,6vw,5rem)] leading-[1.02] tracking-tighter text-[#0F172A]">
              {titles.map((txt, i) => (
                <span key={i} className="block overflow-hidden">
                  <motion.span custom={i} variants={line} initial="hidden" animate="visible" className={`block ${i === 2 ? "text-emerald-500" : ""}`}>
                    {txt}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p custom={0} variants={fade} initial="hidden" animate="visible" className="mt-7 max-w-xl text-base lg:text-lg leading-relaxed text-slate-600">
              {t.hero.subtitle}
            </motion.p>

            <motion.div custom={1} variants={fade} initial="hidden" animate="visible" className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/register" className="group inline-flex items-center gap-2 rounded-full bg-emerald-500 pl-6 pr-5 py-3.5 text-sm font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors duration-300" data-testid="hero-cta">
                {t.hero.cta}
                <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <a href="#how" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3.5 text-sm font-semibold text-[#0F172A] hover:border-[#0F172A] transition-colors duration-300" data-testid="hero-secondary">
                {t.hero.secondary}
              </a>
            </motion.div>

            <motion.div custom={2} variants={fade} initial="hidden" animate="visible" className="mt-14 grid grid-cols-3 gap-6 border-t border-slate-200 pt-8 max-w-lg">
              {[["10", t.hero.stat1], ["100%", t.hero.stat2], ["24/7", t.hero.stat3]].map(([n, label]) => (
                <div key={label}>
                  <div className="font-display font-bold text-3xl text-[#0F172A]">{n}</div>
                  <div className="mt-1 text-xs leading-snug text-slate-500">{label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right — clipped industrial photo */}
          <div className="lg:col-span-6 relative pb-16 lg:pb-24">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="relative aspect-[4/5] w-full overflow-hidden rounded-md border border-slate-200"
              style={{ clipPath: "polygon(0 0, 100% 0, 100% 92%, 92% 100%, 0 100%)" }}
            >
              <motion.img src={IMAGES.heroRobot} alt="Industrial machinery" style={{ y: imgY, scale: imgScale }} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1A]/40 via-transparent to-transparent" />
            </motion.div>

            {/* QR motif card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="absolute -bottom-6 -left-4 lg:-left-10 bg-white border border-slate-200 rounded-md p-4 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.25)] w-56"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-[4px] bg-[#0A0F1A]">
                  <QrCode size={26} weight="bold" className="text-emerald-500" />
                </div>
                <div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-wider text-slate-400">Targa digitale</div>
                  <div className="font-display font-bold text-sm text-[#0F172A]">MX-2024-0417</div>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1.5 border-t border-slate-100 pt-3">
                <ShieldCheck size={16} weight="fill" className="text-emerald-500" />
                <span className="text-xs font-medium text-slate-600">Conforme UE 2023/1230</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
